import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  type CollectionReference,
  type DocumentData,
  type DocumentReference,
} from "firebase/firestore";

import { db } from "@/apis/config/firebase";
import { BACKSTOP_ROLE_ID } from "@/features/rbac/permissions/systemRoles";
import { RBAC_ASSIGNMENTS_COLLECTION } from "@/features/rbac/services/assignmentsService";
import { ensureSystemRoles } from "@/features/rbac/services/rolesService";
import { createAuthAccount } from "@/features/staff/services/staffAccountCreation";
import {
  createStaffMemberSchema,
  staffMemberSchema,
  updateStaffMemberSchema,
  type CreateStaffMemberInput,
  type StaffMember,
  type UpdateStaffMemberInput,
} from "@/features/staff/schemas/staff";

export const STAFF_COLLECTION = "staff";

function getStaffCollection(): CollectionReference<DocumentData> {
  return collection(db, STAFF_COLLECTION);
}

function getStaffDoc(uid: string): DocumentReference<DocumentData> {
  return doc(db, STAFF_COLLECTION, uid);
}

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "object" && "toDate" in value) {
    const candidate = (value as { toDate: () => Date }).toDate();
    return candidate instanceof Date ? candidate : null;
  }
  return null;
}

function mapStaffMember(uid: string, data: DocumentData): StaffMember | null {
  const parsed = staffMemberSchema.safeParse({
    uid,
    email: typeof data.email === "string" ? data.email : "",
    name: typeof data.name === "string" ? data.name : "",
    position: typeof data.position === "string" ? data.position : "",
    active: data.active !== false,
    migratedFromUserUid:
      typeof data.migrated_from_user_uid === "string"
        ? data.migrated_from_user_uid
        : null,
    createdAt: toDate(data.created_at),
    updatedAt: toDate(data.updated_at),
    updatedBy: typeof data.updated_by === "string" ? data.updated_by : null,
  });

  return parsed.success ? parsed.data : null;
}

/** Miembro del equipo, o `null` si el uid no pertenece al equipo. */
export async function getStaffMember(uid: string): Promise<StaffMember | null> {
  if (!uid) return null;

  const snapshot = await getDoc(getStaffDoc(uid));
  if (!snapshot.exists()) return null;

  return mapStaffMember(snapshot.id, snapshot.data());
}

/** Todo el equipo, ordenado por nombre. */
export async function listStaffMembers(): Promise<StaffMember[]> {
  const snapshot = await getDocs(getStaffCollection());

  return snapshot.docs
    .map((docSnap) => mapStaffMember(docSnap.id, docSnap.data()))
    .filter((member): member is StaffMember => member !== null)
    .sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email, "es"));
}

/**
 * Da de alta a un miembro del equipo.
 *
 * El `uid` debe ser el de una cuenta de Firebase Auth que ya exista: esta
 * función NO crea la cuenta de acceso, solo el perfil de equipo. Crear la
 * cuenta de Auth con su contraseña es responsabilidad del flujo de alta.
 */
export async function createStaffMember(
  input: CreateStaffMemberInput,
  actorUid: string | null
): Promise<void> {
  const parsed = createStaffMemberSchema.parse(input);

  const existing = await getStaffMember(parsed.uid);
  if (existing) {
    throw new Error(`La cuenta ${parsed.email} ya forma parte del equipo.`);
  }

  await setDoc(getStaffDoc(parsed.uid), {
    email: parsed.email,
    name: parsed.name,
    position: parsed.position,
    active: parsed.active,
    migrated_from_user_uid: parsed.migratedFromUserUid,
    created_at: new Date(),
    updated_at: new Date(),
    updated_by: actorUid,
  });
}

export async function updateStaffMember(
  uid: string,
  input: UpdateStaffMemberInput,
  actorUid: string | null
): Promise<void> {
  const parsed = updateStaffMemberSchema.parse(input);
  const payload: Record<string, unknown> = {};

  if (parsed.name !== undefined) payload.name = parsed.name;
  if (parsed.position !== undefined) payload.position = parsed.position;
  if (parsed.active !== undefined) payload.active = parsed.active;

  if (Object.keys(payload).length === 0) return;

  payload.updated_at = new Date();
  payload.updated_by = actorUid;

  await updateDoc(getStaffDoc(uid), payload);
}

/**
 * Elimina el perfil de equipo. No borra la cuenta de Firebase Auth: para
 * revocar el acceso conservando el rastro, es preferible desactivar.
 */
export async function deleteStaffMember(uid: string): Promise<void> {
  await deleteDoc(getStaffDoc(uid));
}

/**
 * El PRIMER miembro del equipo recibe el rol de administrador automáticamente.
 *
 * Sin esto el alta inicial es una trampa: en cuanto existe un miembro, el gate
 * deja de aceptar la vía antigua, así que la cuenta que acaba de entrar queda
 * fuera y la recién creada no tiene ningún permiso. Nadie podría administrar
 * nada y la única salida sería editar Firestore a mano.
 *
 * A partir del segundo miembro no se concede nada: las cuentas nacen sin
 * permisos y el acceso se da asignando roles.
 */
async function grantBootstrapRoleIfFirst(
  uid: string,
  email: string,
  actorUid: string | null
): Promise<boolean> {
  const members = await listStaffMembers();
  if (members.length !== 1 || members[0].uid !== uid) return false;

  // Los roles deben existir antes de asignarlos: si es un despliegue nuevo,
  // puede que todavía no se hayan sembrado.
  await ensureSystemRoles(actorUid);

  await setDoc(doc(db, RBAC_ASSIGNMENTS_COLLECTION, uid), {
    subject_uid: uid,
    subject_type: "staff",
    subject_email: email,
    role_ids: [BACKSTOP_ROLE_ID],
    updated_at: new Date(),
    updated_by: actorUid,
  });

  return true;
}

export interface ProvisionStaffMemberInput {
  email: string;
  password: string;
  name: string;
  position?: string;
}

/**
 * Alta completa de un miembro del equipo: cuenta de acceso + perfil.
 *
 * La cuenta se crea en una app de Firebase secundaria para no expulsar de su
 * sesión a quien está dando el alta (ver `staffAccountCreation.ts`).
 *
 * Si la creación del perfil fallara después de crear la cuenta, queda una
 * cuenta de Auth huérfana: no concede acceso al panel —el gate exige documento
 * en `staff`—, pero conviene saberlo, porque reintentar con el mismo correo
 * dará "ya existe una cuenta". Borrar cuentas de Auth requiere el Admin SDK,
 * así que ese caso se resuelve desde la consola de Firebase.
 */
export async function provisionStaffMember(
  input: ProvisionStaffMemberInput,
  actorUid: string | null
): Promise<{ uid: string; grantedAdminRole: boolean }> {
  const email = input.email.trim().toLowerCase();

  // Validar antes de crear nada en Auth: es lo único que no se puede deshacer
  // desde el cliente.
  createStaffMemberSchema.parse({
    uid: "placeholder",
    email,
    name: input.name,
    position: input.position ?? "",
  });

  const { uid } = await createAuthAccount(email, input.password);

  await createStaffMember(
    {
      uid,
      email,
      name: input.name,
      position: input.position ?? "",
    },
    actorUid
  );

  const grantedAdminRole = await grantBootstrapRoleIfFirst(uid, email, actorUid);

  return { uid, grantedAdminRole };
}
